import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

export type SupportedLanguage = 'en' | 'lo';

@Injectable({
  providedIn: 'root',
})
export class LanguageService {
  private readonly STORAGE_KEY = 'app_language';
  private readonly SUPPORTED_LANGUAGES: SupportedLanguage[] = ['en', 'lo'];
  private readonly DEFAULT_LANGUAGE: SupportedLanguage = 'en';

  constructor(private translate: TranslateService) {
    this.initializeLanguage();
  }

  /**
   * Initialize the language from localStorage or use default
   */
  private initializeLanguage(): void {
    const savedLanguage = this.getSavedLanguage();
    const languageToUse = savedLanguage || this.DEFAULT_LANGUAGE;

    this.translate.setDefaultLang(this.DEFAULT_LANGUAGE);
    this.translate.use(languageToUse);
  }

  /**
   * Get the currently active language
   */
  getCurrentLanguage(): SupportedLanguage {
    return this.translate.currentLang as SupportedLanguage;
  }

  /**
   * Get all supported languages
   */
  getSupportedLanguages(): SupportedLanguage[] {
    return this.SUPPORTED_LANGUAGES;
  }

  /**
   * Change the application language
   */
  setLanguage(language: SupportedLanguage): void {
    if (this.SUPPORTED_LANGUAGES.includes(language)) {
      this.translate.use(language);
      this.saveLanguage(language);
    }
  }

  /**
   * Get saved language from localStorage
   */
  private getSavedLanguage(): SupportedLanguage | null {
    const saved = localStorage.getItem(this.STORAGE_KEY);
    if (saved && this.SUPPORTED_LANGUAGES.includes(saved as SupportedLanguage)) {
      return saved as SupportedLanguage;
    }
    return null;
  }

  /**
   * Save language to localStorage
   */
  private saveLanguage(language: SupportedLanguage): void {
    localStorage.setItem(this.STORAGE_KEY, language);
  }

  /**
   * Get translation for a key
   */
  translate$(key: string, params?: any): string {
    return this.translate.instant(key, params);
  }

  /**
   * Get language display name
   */
  getLanguageDisplayName(language: SupportedLanguage): string {
    const displayNames: Record<SupportedLanguage, string> = {
      en: 'English',
      lo: 'ລາວ (Lao)',
    };
    return displayNames[language] || language;
  }
}
